/**
 * Application Entry Point
 *
 * This file initializes the React application with:
 * - TenantProvider for multi-tenancy support
 * - Error boundary for graceful error handling
 *
 * @see src/context/TenantContext.tsx for tenant configuration
 * @see CLAUDE.md Architecture Standards for multi-tenancy rules
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { TenantProvider } from './context/TenantContext';
import { ThemeProvider } from './context/ThemeContext';
import { ensureTenantFontsLoaded } from './branding';
import { getTenantIdSync } from './hooks/useTenantId';
import { applyTenantQueryParam } from './lib/tenant';
import './index.css';

// `/?tenant=acme-gates` selects a tenant before anything renders — branding,
// the login picker, and every getTenantIdSync() consumer see the right
// tenant on first paint. No-op without the param. Must run before
// ensureTenantFontsLoaded / TenantProvider mount below.
applyTenantQueryParam();

// Hydrate the tenant's brand fonts (Poppins for techery-au) at app boot
// so every surface gets them — magazine renderer in the customer viewer,
// the editor preview, the dev hub, future tenant-specific UI. Idempotent;
// re-mounts are no-ops. Pre-tenant-resolution since getTenantIdSync()
// reads localStorage directly — runs before React renders, no flash of
// wrong-font on first paint.
ensureTenantFontsLoaded(getTenantIdSync());

// Handle stale chunk errors after deployment (browser cached old HTML with
// outdated chunk hashes). Two-stage recovery with a visible escape hatch:
//
//   1st chunk error in window → simple reload (covers the common case where
//                               a deploy landed and the HTML is now fresh).
//   2nd chunk error in window → nuke caches + unregister SW + reload
//                               (covers stuck SW serving stale HTML).
//   3rd chunk error in window → render a recovery UI so the user has a
//                               button to tap instead of a blank screen.
//
// The window resets after 30s of no chunk errors. sessionStorage survives
// same-tab refreshes (which is exactly the failure mode of the previous
// one-shot guard: once tripped, F5 didn't clear it and the screen stayed
// blank until the tab was closed).
const CHUNK_RELOAD_KEY = 'theo_chunk_reload_state';
const CHUNK_RELOAD_WINDOW_MS = 30_000;

interface ChunkReloadState {
  attempts: number;
  lastAt: number;
}

function readReloadState(): ChunkReloadState {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.attempts === 'number' && typeof parsed?.lastAt === 'number') {
        return parsed;
      }
    }
  } catch {
    /* fall through */
  }
  return { attempts: 0, lastAt: 0 };
}

function writeReloadState(state: ChunkReloadState) {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(state));
  } catch {
    /* sessionStorage can be unavailable in some embedded contexts */
  }
}

function isChunkLoadError(msg: string | undefined): boolean {
  if (!msg) return false;
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

async function nukeAndReload() {
  console.warn('[chunk-reload] Hard recovery — clearing caches + unregistering SW');
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* best-effort */
  }
  try {
    if ('caches' in self) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* best-effort */
  }
  // Force a no-cache fetch on reload so we don't pick up the cached HTML.
  window.location.reload();
}

function showRecoveryUI() {
  const root = document.getElementById('root');
  if (!root || root.dataset.theoRecoveryShown === '1') return;
  root.dataset.theoRecoveryShown = '1';
  root.innerHTML = `
    <div style="min-height:100vh;background:#0f1521;color:#fff;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,system-ui,sans-serif;padding:24px;">
      <div style="max-width:420px;text-align:center;">
        <div style="font-size:48px;margin-bottom:16px;">↻</div>
        <h2 style="font-size:22px;margin:0 0 12px;font-weight:600;">App needs a clean refresh</h2>
        <p style="color:#94a3b8;margin:0 0 24px;line-height:1.5;font-size:15px;">
          A new version was deployed and your browser is holding onto stale code. Tap below to clear it.
        </p>
        <button id="theo-recover-btn" style="background:#c5a065;color:#0f1521;border:0;padding:14px 28px;border-radius:8px;font-weight:600;font-size:16px;cursor:pointer;width:100%;max-width:280px;">
          Reset &amp; reload
        </button>
        <p style="color:#64748b;margin:20px 0 0;font-size:12px;line-height:1.5;">
          Still stuck after this? Close all tabs and reopen the app.
        </p>
      </div>
    </div>
  `;
  const btn = document.getElementById('theo-recover-btn');
  if (btn) {
    btn.addEventListener('click', () => {
      // Reset the counter so the next attempt starts fresh.
      try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* */ }
      void nukeAndReload();
    });
  }
}

let chunkRecoveryInFlight = false;
function handleChunkError() {
  if (chunkRecoveryInFlight) return;
  chunkRecoveryInFlight = true;

  const now = Date.now();
  const prev = readReloadState();
  const expired = prev.attempts > 0 && now - prev.lastAt > CHUNK_RELOAD_WINDOW_MS;
  const attempts = (expired ? 0 : prev.attempts) + 1;
  writeReloadState({ attempts, lastAt: now });

  if (attempts === 1) {
    console.warn('[chunk-reload] Chunk load failed — soft reload');
    window.location.reload();
  } else if (attempts === 2) {
    console.warn('[chunk-reload] Chunk load failed again — hard recovery');
    void nukeAndReload();
  } else {
    console.error('[chunk-reload] Chunk load still failing after recovery — showing recovery UI');
    chunkRecoveryInFlight = false; // allow recovery UI to be re-shown / button to retry
    showRecoveryUI();
  }
}

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.message)) {
    handleChunkError();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason);
  if (isChunkLoadError(msg)) {
    handleChunkError();
  }
});

// If we made it 30s past mount without another chunk error, clear the
// reload state so a future deploy starts the counter fresh. Without this,
// a long-lived tab that hit a single reload an hour ago would jump
// straight to the hard-recovery path on its next chunk error.
window.addEventListener('load', () => {
  setTimeout(() => {
    const state = readReloadState();
    if (state.attempts > 0 && Date.now() - state.lastAt > CHUNK_RELOAD_WINDOW_MS) {
      try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* */ }
    }
  }, CHUNK_RELOAD_WINDOW_MS);
});

// Add error handling for React initialization
try {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    console.error('[React] Root element not found!');
    throw new Error('Root element #root not found in DOM');
  }

  console.log('[React] Mounting application...');

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <TenantProvider>
          <App />
        </TenantProvider>
      </ThemeProvider>
    </React.StrictMode>
  );

  console.log('[React] Application mounted successfully');
} catch (error) {
  console.error('[React] Failed to mount application:', error);

  // Show error in the UI
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="padding: 40px; text-align: center; color: #ef4444;">
        <h2>Failed to Initialize Dashboard</h2>
        <p>Error: ${error instanceof Error ? error.message : String(error)}</p>
        <pre style="text-align: left; background: #f3f4f6; padding: 20px; border-radius: 8px; overflow: auto;">
${error instanceof Error ? error.stack : String(error)}
        </pre>
      </div>
    `;
  }

  // Re-throw so it appears in console
  throw error;
}
